import { SyntheticEvent, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Highcharts from 'highcharts';
import merge from 'lodash/merge';

import * as K from 'common/constants';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { getBaseGraphConfig } from 'components/common/Graphs/graphConfigs';
import { getFormattedTimeOffsetUTC, getTimezoneOffset, parseDateTime } from 'common/util/time';
import { HoverData } from 'common/types';
import { AlertInfo, AlertStatus } from 'common/graphqlTypes';
import ELEMENT from 'common/element';
import ScalyrHighcharts from 'components/common/ScalyrHighcharts';

import './TimeSeriesGraph.scss';

type TimeSeriesGraphProps = {
  data: Array<[number | undefined, number | undefined]>;
  threshold: string;
  alert: AlertInfo;
};

/**
 * The Time Series Graph that contains config and renders a column graph with a threshold plot line
 *
 * @prop data                 Array of tuples containing [x,y] plot values for the graph series
 * @prop threshold            The threshold value for rendering the threshold line
 */

function TimeSeriesGraph({ data, threshold, alert }: TimeSeriesGraphProps): JSX.Element {
  const [hoverData, setHoverData] = useState<HoverData | null>(null);
  const timezone = useRecoilValue(timezoneState);
  const timezoneOffset = getTimezoneOffset(timezone);

  // Need to set the timezone option globally since we are using Highcharts.dateFormat()
  Highcharts.setOptions({
    global: {
      ...(timezone ? { timezoneOffset } : {}),
      // @ts-expect-error
      useUTC: !!timezone,
    },
  });

  // This determines the maximum value for the y-axis. When the threshold is larger, the threshold value should be the maximum.
  // Otherwise, it should default to HighCharts to determine the range based on the series data.
  const getYMax = (): string | undefined => {
    if (!data) {
      return undefined;
    }

    const yValues = data.map((plots) => plots[1] || 0);
    const highestYValue = Math.max(...yValues);
    return parseInt(threshold, 10) > highestYValue ? threshold : undefined;
  };

  const timeSeriesGraphConfig = {
    yAxis: {
      gridLineWidth: 0.5,
      offset: 20,
      minorGridLineWidth: 0,
      tickPixelInterval: 10,
      tickWidth: 0,
      lineWidth: 0,
      minPadding: 0.5,
      maxPadding: 0,
      startOnTick: false,
      endOnTick: false,
      labels: {
        align: 'left',
        x: -10,
        enabled: true,
      },
      plotLines: [
        {
          value: threshold,
          className: 'TimeSeriesGraph__ThresholdLine',
          dashStyle: 'shortdash',
          width: 2,
          zIndex: 4,
        },
      ],
      max: getYMax(),
    },
    plotOptions: {
      [K.SERIES_TYPES.COLUMN]: {
        animation: false,
        pointPadding: 0,
        groupPadding: 0.1,
        borderWidth: 0,
      },
      series: {
        point: {
          events: {
            mouseOver: (e: SyntheticEvent): void => {
              const target = e.target as typeof e.target & {
                x: number;
                y: number;
              };
              setHoverData({
                value: Math.round(target.y).toLocaleString(),
                time: Highcharts.dateFormat('%b %e · %l:%M %p', target.x),
              });
            },
            mouseOut: (): void => {
              setHoverData(null);
            },
          },
        },
      },
    },
    series: [{ data }],
    chart: {
      type: K.SERIES_TYPES.COLUMN,
    },
  };

  const options = merge(getBaseGraphConfig(), timeSeriesGraphConfig);

  const renderResolvedStatus = (): JSX.Element | null => {
    if (alert?.streakStartNs === -1) {
      return null;
    }

    const time = parseDateTime(alert?.streakStartNs, null, null, timezone)?.moment;
    const offset = getFormattedTimeOffsetUTC(timezoneOffset);
    const startStreakFormattedDate = time?.format('MMM DD, h:mm a');

    return alert?.lastStatus === AlertStatus.TRIGGERED && time ? (
      <div>{`Triggered since ${startStreakFormattedDate} [${offset}]`}</div>
    ) : (
      <div>{`Resolved since ${startStreakFormattedDate} [${offset}]`}</div>
    );
  };

  const renderHoverData = hoverData ? <p>{`${hoverData.value} on ${hoverData.time}`}</p> : renderResolvedStatus();

  return (
    <div className="TimeSeriesGraph" data-test-id={ELEMENT.alertDetailsTimeSeriesGraph}>
      <ScalyrHighcharts
        options={options}
        highcharts={Highcharts}
        containerProps={{ style: { height: '100%', width: '100%' } }}
      />
      {renderHoverData}
    </div>
  );
}

export default TimeSeriesGraph;
